import React, { useState } from 'react';

import { Check, ExternalLink, Share2 } from 'lucide-react';
import ImportantTermText from '../ImportantTermText/ImportantTermText';

import './NewsArticleReader.css';

const formatDate = value => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata', day: 'numeric', month: 'long', year: 'numeric',
  });
};

const decodeEntities = value => {
  if (!value) return '';
  const entities = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&nbsp;': ' ' };
  return String(value).replace(/&(?:amp|lt|gt|quot|#39|nbsp);/g, entity => entities[entity] || entity);
};

/* List items can be plain text or small objects */
const itemText = v => (typeof v === 'string' ? v : (v && (v.text || v.term || v.title || v.point)) || '');

function formatPrelimsQuestion(value) {
  const question = String(value || '').replace(/\r\n?/g, '\n').trim();
  const statementMarker = /(?:\(?[1-4]\)?[.)]|statement\s+[1-4]\s*[:.)]|[IVX]{1,3}[.)])\s+/gi;
  const markers = [...question.matchAll(statementMarker)];
  if (markers.length < 2) return question;

  const formatted = question.replace(
    /[ \t]+(?=(?:\(?[1-4]\)?[.)]|statement\s+[1-4]\s*[:.)]|[IVX]{1,3}[.)])\s+)/gi,
    '\n'
  );
  return formatted.replace(/([.!?])[ \t]+(?=(?:Which|How many|Select|Choose)\b)/i, '$1\n');
}

export default function NewsArticleReader({ article, explanation, onShare }) {
  const [linkCopied, setLinkCopied] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [mainsDraft, setMainsDraft] = useState('');
  const structured = explanation && typeof explanation === 'object'
    ? explanation
    : article.upscExplanationStructured;
  const story = decodeEntities(structured?.newsStory || article.description || '');
  const paragraphs = story.split(/\n\s*\n/).map(part => part.trim()).filter(Boolean);
  const whyUpsc = structured?.whyUpsc || '';
  const keyTerms = Array.isArray(structured?.keyTerms) ? structured.keyTerms : [];
  const keyPoints = Array.isArray(structured?.keyPoints) ? structured.keyPoints : [];
  const traps = Array.isArray(structured?.upscTraps) ? structured.upscTraps : [];
  const sourceUrl = (article.url || '').replace(
    /https?:\/\/www\.pib\.gov\.in\/PressReleaseIframePage\.aspx\?PRID=(\d+)[^#]*/i,
    'https://www.pib.gov.in/PressReleasePage.aspx?PRID=$1&reg=3&lang=1'
  );
  const legacyExplanation = typeof explanation === 'string' ? explanation : '';
  const hasUpscContext = whyUpsc || keyTerms.length || keyPoints.length || traps.length || legacyExplanation;
  const prelimsQuestion = structured?.prelimsQuestion;
  const mainsQuestion = structured?.mainsQuestion;
  const showImage = Boolean(article.imageUrl) && !imageFailed;
  const title = decodeEntities(article.title);
  const meta = [article.source, structured?.gsMapping].filter(Boolean).join(' · ');
  const words = mainsDraft.trim() ? mainsDraft.trim().split(/\s+/).length : 0;

  const copyLink = async () => {
    const shareUrl = `${window.location.origin}/news/${article._id}`;
    try {
      if (onShare) await onShare(article);
      await copyText(shareUrl);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 1800);
    } catch {
      setLinkCopied(false);
    }
  };

  return (
    <div className="news-reader-wrap">
      <article className="news-reader">
        {showImage && (
          <figure className="news-reader-figure">
            <img src={article.imageUrl} alt={title} loading="lazy" onError={() => setImageFailed(true)} />
          </figure>
        )}

        <header className="news-reader-head">
          <div className="news-reader-top">
            <span className="news-reader-chip">{article.subject || 'Current Affairs'}</span>
            {article.publishedAt && <time dateTime={article.publishedAt}>{formatDate(article.publishedAt)}</time>}
          </div>
          <div className="news-reader-head-row">
            <h1 className="news-reader-title">{title}</h1>
            {(onShare || sourceUrl) && (
              <div className="news-reader-actions">
                {onShare && (
                  <button type="button" onClick={copyLink} aria-label="Copy share link">
                    {linkCopied ? <Check size={15} /> : <Share2 size={15} />}
                    {linkCopied ? 'Link copied' : 'Share'}
                  </button>
                )}
                {sourceUrl && (
                  <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={15} /> Source
                  </a>
                )}
              </div>
            )}
          </div>
          {meta && <p className="news-reader-meta">{meta}</p>}
        </header>

        {structured?.what && <p className="news-reader-lead"><ImportantTermText text={decodeEntities(structured.what)} /></p>}

        <div className="news-reader-story">
          {paragraphs.map((paragraph, index) => (
            <p key={`${index}-${paragraph.slice(0, 24)}`}><ImportantTermText text={decodeEntities(paragraph)} /></p>
          ))}
        </div>

        {hasUpscContext && (
          <section className="news-reader-context" aria-label="UPSC context">
            <h2>UPSC connection</h2>
            {whyUpsc && <p className="news-reader-why"><ImportantTermText text={whyUpsc} /></p>}
            {legacyExplanation && !whyUpsc && <p className="news-reader-why"><ImportantTermText text={legacyExplanation} /></p>}
            {keyPoints.length > 0 && (
              <div className="news-reader-keypoints">
                <h3>Important points</h3>
                <ul>{keyPoints.map((point, index) => <li key={index}><ImportantTermText text={itemText(point)} /></li>)}</ul>
              </div>
            )}
            {keyTerms.length > 0 && (
              <div className="news-reader-terms">
                {keyTerms.map((term, index) => <p key={index}><ImportantTermText text={itemText(term)} /></p>)}
              </div>
            )}
            {traps.length > 0 && (
              <div className="news-reader-traps">
                <h3>Important distinctions</h3>
                <ul>{traps.map((trap, index) => <li key={index}><ImportantTermText text={itemText(trap)} /></li>)}</ul>
              </div>
            )}
          </section>
        )}

        {prelimsQuestion?.options?.length === 4 && (
          <section className="news-reader-question" aria-label="UPSC Prelims practice question">
            <h2>Prelims practice</h2>
            <p className="news-reader-question-stem"><ImportantTermText text={formatPrelimsQuestion(prelimsQuestion.question)} /></p>
            <div className="news-reader-options">
              {prelimsQuestion.options.map((option, index) => {
                const revealed = selectedAnswer !== null;
                const state = revealed && option.isCorrect
                  ? 'correct'
                  : selectedAnswer === index
                    ? 'selected-wrong'
                    : '';
                return (
                  <button
                    key={index}
                    type="button"
                    className={`news-reader-option ${state}`}
                    onClick={() => setSelectedAnswer(index)}
                    disabled={revealed}
                  >
                    <span className="news-reader-option-letter">{String.fromCharCode(65 + index)}</span>
                    <span>{itemText(option)}</span>
                  </button>
                );
              })}
            </div>
            {selectedAnswer !== null && (
              <div className="news-reader-answer">
                <strong>{prelimsQuestion.options[selectedAnswer]?.isCorrect ? 'Correct answer' : 'Review the correct option'}</strong>
                <p><ImportantTermText text={prelimsQuestion.explanation} /></p>
              </div>
            )}
          </section>
        )}

        {mainsQuestion && (
          <section className="news-reader-mains" aria-label="UPSC Mains practice question">
            <h2>Mains practice</h2>
            <p className="news-reader-question-stem"><ImportantTermText text={mainsQuestion} /></p>
            <textarea
              value={mainsDraft}
              onChange={event => setMainsDraft(event.target.value)}
              rows={5}
              placeholder="Write your answer"
              aria-label="Your answer"
            />
            <span className="news-reader-word-count">{words} words</span>
          </section>
        )}
      </article>
    </div>
  );
}

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const input = document.createElement('textarea');
  input.value = value;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();
  const copied = document.execCommand('copy');
  input.remove();
  if (!copied) throw new Error('Clipboard copy failed');
}